import { usePermissions } from '@/hooks/usePermissions'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import {
  Box,
  HStack,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react'

import { AlarmAssociation, DeviceAssociation } from '@/components/icons'
import { Modal } from '@/components/overlays'
import { Button } from '@/components/ui'
import { useDoorTailgateGridsEnabledQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

import { AssociateDeviceI, ConnectionI } from '../../types/types'
import { isTailgatingEnabledForOtherDevices } from '../utils/utils'
import { DeviceDetails } from './DeviceDetails'
import { EnableTailgateBox } from './EnableTailgateBox'
import { TailgateAlertLabel } from './TailgateAlertLabel'

interface IProps {
  associateDevice: AssociateDeviceI
  connection: ConnectionI
  loading: boolean
  isEnableTailgateLoading: boolean
  isOpen: boolean
  hideAssociateButton: boolean
  onClose: () => void
  onAssociate?: () => void
  onDisassociate: () => void
  onEnableTailgate: (isTailgateEnabled: boolean) => void
  onOpenGrid: () => void
}

export const AssociateDeviceIcon = (deviceType: DeviceType) => {
  return deviceType === DeviceType.Door ? DeviceAssociation : AlarmAssociation
}

export const AssociateDeviceModal = ({
  associateDevice,
  connection,
  loading,
  isEnableTailgateLoading,
  isOpen,
  hideAssociateButton,
  onClose,
  onAssociate,
  onDisassociate,
  onEnableTailgate,
  onOpenGrid,
}: IProps) => {
  const { deviceType, cameraName, doorName, doorId, cameraId } = associateDevice
  const { isConnected, isTailgateGridConfigured, isTailgateEnabled } =
    connection || {}

  const { data, loading: isTailgateGridsLoading } =
    useDoorTailgateGridsEnabledQuery({
      fetchPolicy: 'network-only',
      skip: !isConnected,
      variables: { id: doorId },
    })
  const hasExistingTailgateEnabled = isTailgatingEnabledForOtherDevices(
    data,
    cameraId
  )
  const { shouldEnableAssociateDevices, shouldEnableConfigureTailgateGrid } =
    usePermissions()

  const title = 'Edit Device Association'

  const middleButtonText = isConnected
    ? 'Disassociate Devices'
    : 'Associate Devices'

  const loadingText = isConnected
    ? 'Disassociating Devices'
    : 'Associating Devices'

  const shouldDisableTailgate =
    !isConnected || // The devices are not associated
    !isTailgateGridConfigured || // The grid is not configured
    (hasExistingTailgateEnabled && !isTailgateEnabled) // tailgate is enabled already on a different device + not enabled for this device (This allows use to disable it but not enable)

  const isConfigureGridsDisabled =
    !isConnected || // The devices are not associated
    !shouldEnableConfigureTailgateGrid || // has permission
    loading ||
    isEnableTailgateLoading

  // Only Doors & Cameras can configure grids. This will have issues in future if we allow something like Doors &
  // Alarms to be connected, but for now it's safe to assume that if we've gotten this far we should show this button.
  const canConfigureGrids =
    deviceType === DeviceType.Door || deviceType === DeviceType.Camera

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='2xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff' overflow='hidden' py='10px'>
        <ModalCloseButton color='#000' opacity={0.4} />
        <ModalHeader>
          <HStack spacing='15px'>
            <Box
              color='#172B4D'
              data-testid='AssociateDeviceModal:header'
              fontSize='26px'
              fontWeight='extrabold'
              letterSpacing='-0.4px'
              lineHeight='1'
            >
              {title}
            </Box>
          </HStack>
        </ModalHeader>
        <ModalBody>
          <DeviceDetails cameraName={cameraName} deviceName={doorName} />
          <Text mb='5px'>Features:</Text>
          <EnableTailgateBox
            isDisabled={shouldDisableTailgate}
            isEnabled={isTailgateEnabled}
            isLoading={isEnableTailgateLoading || isTailgateGridsLoading}
            onToggleChange={onEnableTailgate}
          />
          {!hideAssociateButton && isTailgateGridConfigured && (
            <TailgateAlertLabel
              boldText='DISABLE'
              endText='tailgating alerts and camera grids'
              startText='Disassociating devices will '
            />
          )}
          {hasExistingTailgateEnabled && (
            <TailgateAlertLabel startText='Tailgating is already enabled on a different camera.' />
          )}
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={onClose} variant='secondary'>
            Cancel
          </Button>

          <Spacer />

          {/* region Associate/Disassociate button */}
          {!hideAssociateButton && (
            <Button
              isDisabled={
                !shouldEnableAssociateDevices || isEnableTailgateLoading
              }
              isLoading={loading}
              leftIcon={
                <Icon
                  as={AssociateDeviceIcon(DeviceType.Door)}
                  boxSize={26}
                  strokeColor='#fff'
                />
              }
              loadingText={loadingText}
              mr={3}
              onClick={() => {
                isConnected ? onDisassociate() : onAssociate?.()
              }}
              variant={isConnected ? 'danger' : 'primary'}
            >
              {middleButtonText}
            </Button>
          )}
          {/* endregion */}

          {/* region Edit Grids buton */}
          {canConfigureGrids && (
            <Button
              isDisabled={isConfigureGridsDisabled}
              leftIcon={<HiOutlineCheckCircle size='24px' />}
              onClick={onOpenGrid}
              variant='primary'
            >
              {isTailgateGridConfigured
                ? 'Edit Camera Grids'
                : 'Configure Camera Grids'}
            </Button>
          )}
          {/* endregion */}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
